import { useEffect } from 'react';

import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';

import { useExperimentalRelationsForContentQuery } from './useExperimentalRelationsForContentQuery.ts';

const isKnownError = (error: ApolloErrorInterop) => {
	return error.graphQLErrors.some(
		(e) =>
			e.message === 'User does not have permission to view the relationship' ||
			e.message === 'Target entity does not exist',
	);
};

export const useContentRelations = (
	relationName: string,
	contentId: string,
	isUnpublishedDraft: boolean | null,
) => {
	const variables = {
		relationName,
		sourceType: 'user',
		targetKey: contentId,
		targetStatus: 'draft',
	};

	const [getRelations, { data, loading, error }] = useExperimentalRelationsForContentQuery({
		variables,
	});

	useEffect(() => {
		// If we're reverting rather than deleting
		if (!isUnpublishedDraft) {
			getRelations();
		}
	}, [isUnpublishedDraft, getRelations]);

	useEffect(() => {
		if (error && isKnownError(error)) {
			markErrorAsHandled(error);
		}
	}, [error]);

	return {
		relations: data?.experimentalRelationsForContent?.results ?? [],
		loading,
		error,
	};
};
